import {
  OrderChangeOptionsDto,
  OrderChangeRequestStatus
} from '@hconnect/tuc-processes/orderManagement'
import {Contact as TucContact} from '@hconnect/tuc-processes/types'
import {orderBy} from 'lodash'

import {api} from '../../App.global'
import {DistributedCacheResponse} from '../../DistributedStorage/type'
import {RmcOrderPayload} from '../../features/rmc/orderIntake/services/rmcOrderPlacementService'
import {RmcOrderRequestStatusResponse} from '../../features/rmc/orderIntake/utils/mapRmcOrderRequestStatus'
import {LatestOrderChangeRequest} from '../../features/rmc/orderManagement/types/orderChangeBanner.types'
import {CancellationRequestStatus} from '../../features/rmc/orderCancellation/types/cancellationRequest.types'
import {Slot, SlotFilter} from '../../OrderIntake/BulkCement/containers/slotManagement/types'
import {OrderRequestStatus} from '../../OrderIntake/declarations/OrderIntake.enums'
import {
  Contact,
  OptimizedOption,
  OrderIntakeMaterialOptionPayload,
  OrderRequestResponse,
  PersistedOrderRequest,
  SalesOfficeData,
  SlotConfiguration,
  OrderCancellationOptions,
  NotificationSettings,
  OptionsV2
} from '../../OrderIntake/declarations/types'
import {mapOptimizedOptionsToLegacy, mapV2OptionsToLegacy} from '../../OrderIntake/Options/mapper'
import {PersistedRequestForQuoteFormValues} from '../../OrderIntake/RequestForQuoteFormValues'
import {
  sanitizeRfqOffice,
  toPersistedRequestForQuote
} from '../../OrderIntake/BulkCement/RequestForQuote/requestForQuoteMapper'
import {
  ORDERS_MANAGEMENT_API_PATH,
  ORDER_REQUESTS_API_PATH,
  REQUEST_FOR_QUOTES_API_PATH,
  V2_ORDER_REQUESTS_API_PATH
} from '../../Organisms/OrderIntake/OrderIntake.action'
import {FilterDefinition} from '../types'

import {AsyncFn} from './api'
import {ScheduledExportType} from './types'

export interface Queries {
  orderRequestsOptions: AsyncFn<
    [
      {
        customerId?: string
        applyMaterialEnteredDescription: boolean
        applyContractItemDescription: boolean
      }
    ],
    OrderIntakeMaterialOptionPayload[]
  >
  orderRequestsOptionsV2: AsyncFn<
    [
      {
        customerId?: string
        applyMaterialEnteredDescription: boolean
        applyContractItemDescription: boolean
        apiPath?: string
      }
    ],
    OrderIntakeMaterialOptionPayload[]
  >
  failedOrderRequests: AsyncFn<[customerId?: string], OrderRequestResponse[]>
  failedOrderChangeRequests: AsyncFn<[startDate: string], OrderChangeRequestStatus[]>
  dismissedFailedOrderChangeRequests: AsyncFn<[], string[]>
  notValidatedOrderRequests: AsyncFn<[customerId?: string], OrderRequestResponse[]>
  rmcOrderRequestStatuses: AsyncFn<[requestIds: string[]], RmcOrderRequestStatusResponse[]>
  persistedOrderRequests: AsyncFn<[siteNumber: string], PersistedOrderRequest[]>
  persistedContact: AsyncFn<[siteNumber: string], Contact>
  persistedRmcContact: AsyncFn<
    [siteNumber: string],
    {orderPlacer?: TucContact; siteContact?: TucContact}
  >
  persistedRmcOrder: AsyncFn<[siteNumber: string], RmcOrderPayload[] | undefined>
  slotConfigurations: AsyncFn<[], SlotConfiguration[]>
  requestForQuotesRegions: AsyncFn<[country: string], SalesOfficeData[]>
  orderCancellationOptions: AsyncFn<[orderId: string, checkSum?: string], OrderCancellationOptions>
  orderChangeOptions: AsyncFn<[orderId: string, checkSum?: string], OrderChangeOptionsDto>
  cancellationRequestStatus: AsyncFn<[orderId: string], CancellationRequestStatus | null>
  orderChangeRequestStatus: AsyncFn<[orderId: string], OrderChangeRequestStatus>
  orderChangeRequestById: AsyncFn<[orderId: string, requestId: string], OrderChangeRequestStatus>
  latestOrderChangeRequest: AsyncFn<[orderId: string], LatestOrderChangeRequest | null>
  dismissedOrderChangeBanners: AsyncFn<[], string[]>
  dismissedOrderCancellationBanners: AsyncFn<[], string[]>
  persistedRequestForQuote: AsyncFn<
    [siteNumber: string, businessLine: string],
    PersistedRequestForQuoteFormValues | undefined
  >
  notificationSettings: AsyncFn<[], NotificationSettings[]>
  scheduledExports: AsyncFn<[], ScheduledExportType[]>
  persistedFilters: AsyncFn<[filterType: string], FilterDefinition[]>
  getBulkCemSlots: AsyncFn<[filterType: SlotFilter], Slot[]>
}
export const queries: Queries = {
  orderRequestsOptions: async ({
    customerId,
    applyMaterialEnteredDescription,
    applyContractItemDescription
  }) => {
    const path = `${ORDER_REQUESTS_API_PATH}/options?customerId=${customerId}`
    const response = await api.get<OptimizedOption[]>(path)
    const options = mapOptimizedOptionsToLegacy(
      response.data,
      applyMaterialEnteredDescription,
      applyContractItemDescription,
      customerId
    )

    return options
  },
  orderRequestsOptionsV2: async ({
    customerId,
    applyMaterialEnteredDescription,
    applyContractItemDescription,
    apiPath
  }) => {
    const basePath = apiPath ?? V2_ORDER_REQUESTS_API_PATH
    const path = `${basePath}/options?customerId=${customerId}`
    const response = await api.get<OptionsV2>(path)
    const options = mapV2OptionsToLegacy(
      response.data,
      applyMaterialEnteredDescription,
      applyContractItemDescription
    )
    return options
  },
  failedOrderRequests: async (customerId) => {
    const path = `${ORDER_REQUESTS_API_PATH}?customerId=${customerId}&status=${OrderRequestStatus.FAILED}&isBannerDismissed=${false}`
    const response = await api.get<OrderRequestResponse[]>(path)
    return response.data
  },
  failedOrderChangeRequests: async (startDate) => {
    const path = `${ORDERS_MANAGEMENT_API_PATH}/v2/Change/requests?startDate=${startDate}&latestRequestOnly=true&status=failed`
    const response = await api.get<OrderChangeRequestStatus[]>(path)
    return response.data
  },
  dismissedFailedOrderChangeRequests: async (): Promise<string[]> => {
    const path = 'distributeduserstorage?keyIdentifier=orderChange-dismissed-failed-requests'
    const response = await api.get<DistributedCacheResponse>(path)

    if (!response.data.value) return []

    return JSON.parse(response.data.value)
  },
  notValidatedOrderRequests: async (customerId) => {
    const path = `${ORDER_REQUESTS_API_PATH}?customerId=${customerId}&status=${OrderRequestStatus.NOT_VALIDATED}`
    const response = await api.get<OrderRequestResponse[]>(path)
    return response.data
  },
  rmcOrderRequestStatuses: async (requestIds) => {
    return Promise.all(
      requestIds.map(async (requestId) => {
        const response = await api.get<RmcOrderRequestStatusResponse>(
          `${ORDER_REQUESTS_API_PATH}/${requestId}`
        )
        return {...response.data, requestId}
      })
    )
  },
  persistedOrderRequests: async (siteNumber) => {
    const path = `distributeduserstorage?keyIdentifier=orderIntake-orderRequests-${siteNumber}`
    const response = await api.get<DistributedCacheResponse>(path)

    if (!response.data.value) return

    return JSON.parse(response.data.value)
  },
  persistedContact: async (siteNumber) => {
    const path = `distributeduserstorage?keyIdentifier=orderIntake-contact-${siteNumber}`
    const response = await api.get<DistributedCacheResponse>(path)

    if (!response.data.value) return

    return JSON.parse(response.data.value)
  },
  persistedRmcContact: async (siteNumber) => {
    const path = `distributeduserstorage?keyIdentifier=orderIntake-contact-${siteNumber}-RMC`
    const response = await api.get<DistributedCacheResponse>(path)

    if (!response.data.value) return

    return JSON.parse(response.data.value)
  },
  persistedRmcOrder: async (siteNumber) => {
    const path = `distributeduserstorage?keyIdentifier=orderIntake-orderRequests-${siteNumber}-RMC`
    const response = await api.get<DistributedCacheResponse>(path)

    if (!response.data.value) return undefined

    return JSON.parse(response.data.value)
  },
  slotConfigurations: async (): Promise<SlotConfiguration[]> => {
    const path = '/SlotConfiguration/configurations?country=GB'
    const response = await api.get<SlotConfiguration[]>(path)
    return orderBy(
      response.data.filter((slotConfig) => slotConfig.isAlwaysFull === false),
      'slotPosition',
      'asc'
    )
  },
  requestForQuotesRegions: async (country: string): Promise<SalesOfficeData[]> => {
    const path = `${REQUEST_FOR_QUOTES_API_PATH}?country=${country}`
    const response = await api.get<SalesOfficeData[]>(path)
    return response.data.map(sanitizeRfqOffice)
  },
  orderCancellationOptions: async (orderId): Promise<OrderCancellationOptions> => {
    const path = `${ORDERS_MANAGEMENT_API_PATH}/Cancel/options/${orderId}`
    const response = await api.get<OrderCancellationOptions>(path)
    return response.data
  },
  orderChangeOptions: async (orderId): Promise<OrderChangeOptionsDto> => {
    const path = `${ORDERS_MANAGEMENT_API_PATH}/v2/Change/Options/${orderId}`
    const response = await api.get<OrderChangeOptionsDto>(path)
    return response.data
  },
  cancellationRequestStatus: async (orderId): Promise<CancellationRequestStatus | null> => {
    try {
      const path = `${ORDERS_MANAGEMENT_API_PATH}/Cancel/${orderId}/requests`
      const response = await api.get<CancellationRequestStatus>(path)
      return response.data
    } catch (error) {
      if ((error as {response?: {status?: number}})?.response?.status === 404) {
        return null
      }
      throw error
    }
  },
  orderChangeRequestStatus: async (orderId): Promise<OrderChangeRequestStatus> => {
    const path = `${ORDERS_MANAGEMENT_API_PATH}/v2/Change/${orderId}/requests`
    const response = await api.get<OrderChangeRequestStatus>(path)
    return response.data
  },
  orderChangeRequestById: async (orderId, requestId): Promise<OrderChangeRequestStatus> => {
    const path = `${ORDERS_MANAGEMENT_API_PATH}/v2/Change/${orderId}/requests/${requestId}`
    const response = await api.get<OrderChangeRequestStatus>(path)
    return response.data
  },
  latestOrderChangeRequest: async (orderId): Promise<LatestOrderChangeRequest | null> => {
    try {
      const path = `${ORDERS_MANAGEMENT_API_PATH}/v2/Change/${orderId}/requests?latestRequestOnly=true&extend=ChangesApplied`
      const response = await api.get<LatestOrderChangeRequest[]>(path)
      // API returns an array, get the first (and only) item
      return response.data?.[0] ?? null
    } catch (error) {
      // Return null if 404 (no active change request)
      if ((error as {response?: {status?: number}})?.response?.status === 404) {
        return null
      }
      throw error
    }
  },
  dismissedOrderChangeBanners: async (): Promise<string[]> => {
    const path = 'distributeduserstorage?keyIdentifier=orderChange-dismissed-banners'
    const response = await api.get<DistributedCacheResponse>(path)

    if (!response.data.value) return []

    return JSON.parse(response.data.value)
  },
  dismissedOrderCancellationBanners: async (): Promise<string[]> => {
    const path = 'distributeduserstorage?keyIdentifier=orderCancellation-dismissed-banners'
    const response = await api.get<DistributedCacheResponse>(path)

    if (!response.data.value) return []

    return JSON.parse(response.data.value)
  },
  persistedRequestForQuote: async (siteNumber, businessLine) => {
    const path = `distributeduserstorage?keyIdentifier=orderIntake-request-for-quote-${siteNumber}-${businessLine}`
    const response = await api.get<DistributedCacheResponse>(path)

    if (!response.data.value) return

    return toPersistedRequestForQuote(JSON.parse(response.data.value))
  },
  notificationSettings: async (): Promise<NotificationSettings[]> => {
    const path = '/user-subscriptions'
    const response = await api.get<NotificationSettings[]>(path)
    return response.data
  },
  scheduledExports: async (): Promise<ScheduledExportType[]> => {
    const path = '/scheduledexports'
    const response = await api.get<ScheduledExportType[]>(path)
    return response.data.sort(
      (a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime()
    )
  },
  persistedFilters: async (filterType) => {
    const path = `distributeduserstorage?keyIdentifier=orderIntake-filters-${filterType}`
    const response = await api.get<DistributedCacheResponse>(path)
    if (!response.data.value) return
    return JSON.parse(response.data.value)
  },
  getBulkCemSlots: async (filterType: SlotFilter): Promise<Slot[]> => {
    const path = `/slotmanagement/slots?country=${filterType.country}&plantId=${filterType.plantId}&materialNumber=${filterType.materialNumber}&startDate=${filterType.startDate}&endDate=${filterType.endDate}`
    const response = await api.get<Slot[]>(path)
    return response.data
  }
}
